import React from 'react'

const testimonials = [
  {
    name: 'Elena Rostova',
    role: 'Engineering Director @ Novatech',
    category: 'Corporate Training',
    initials: 'ER',
    color: 'from-emerald-500 to-teal-600',
    stars: 5,
    text: "Our corporate engineering team went through NexGenium's advanced Kubernetes & DevOps Masterclass. The customized labs and practical focus saved us weeks of trial and error. Stellar instruction!"
  },
  {
    name: 'Aditya Verma',
    role: 'Cloud DevOps Engineer @ TCS',
    category: 'Individual Masterclass',
    initials: 'AV',
    color: 'from-cyan-500 to-blue-600',
    stars: 5,
    text: 'The individual cloud training program at NexGenium was a game-changer. The hands-on labs with production-grade CI/CD pipelines helped me clear my CKA exam and secure my dream DevOps role.'
  },
  {
    name: 'Sneha Iyer',
    role: 'Tech Lead @ Infosys',
    category: 'Software Service',
    initials: 'SI',
    color: 'from-purple-500 to-indigo-600',
    stars: 5,
    text: 'NexGenium delivered exceptional full-stack software development solutions for our enterprise client. Their engineering team is proactive, highly skilled, and their architecture standards are top-notch.'
  },
  {
    name: 'Marcus Sterling',
    role: 'CTO @ FinTech Solutions',
    category: 'Corporate Training',
    initials: 'MS',
    color: 'from-red-500 to-pink-600',
    stars: 5,
    text: 'NexGenium upskilled our entire backend team in AIOps and AWS cloud-native patterns. The impact was immediate: our deployment efficiency increased by 40% within the first month post-training.'
  },
  {
    name: 'Rajesh Patel',
    role: 'AWS Certified Cloud Architect',
    category: 'Individual Masterclass',
    initials: 'RP',
    color: 'from-amber-500 to-orange-600',
    stars: 5,
    text: 'The Generative AI and LLM integration masterclass was extremely forward-looking. Learning how to deploy models in production environments put me way ahead in the tech industry. Great mentorship!'
  },
  {
    name: 'David Vance',
    role: 'Product Owner @ SaaSify',
    category: 'Software Service',
    initials: 'DV',
    color: 'from-blue-600 to-indigo-700',
    stars: 5,
    text: 'Partnering with NexGenium for our SaaS platform development was the best decision we made. They built a secure, scalable multi-tenant architecture on budget and ahead of schedule.'
  }
]

export default function Reviews() {
  // We duplicate the array 4 times for seamless continuous marquee loop math
  const marqueeItems = [...testimonials, ...testimonials, ...testimonials, ...testimonials]

  return (
    <section className="relative py-28 md:py-36 overflow-hidden bg-[#071630] font-inter border-t border-slate-800 min-h-[500px] lg:min-h-[650px] flex flex-col justify-center">
      {/* Ambient background glows */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-[#00cfff]/10 rounded-full blur-[150px] pointer-events-none"></div>
      <div className="absolute top-12 right-12 w-[300px] h-[300px] bg-blue-500/10 rounded-full blur-[100px] pointer-events-none"></div>

      <div className="relative z-10">
        {/* Header Section */}
        <div className="text-center max-w-3xl mx-auto mb-16 px-6">
          <div className="inline-flex items-center gap-2 bg-white/5 border border-white/10 px-5 py-2 rounded-full mb-6 shadow-sm backdrop-blur-sm">
            <span className="w-2.5 h-2.5 rounded-full bg-[#00cfff] animate-pulse"></span>
            <span className="text-xs font-bold tracking-widest text-[#00cfff] uppercase">Success Stories</span>
          </div>
          
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-white tracking-tight leading-[1.15] mb-6">
            What Our <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#00cfff] to-blue-400">Community</span> Says
          </h2>
          <p className="text-lg md:text-xl text-slate-400 font-medium leading-relaxed">
            Discover how we deliver enterprise software excellence and empower cloud & DevOps careers worldwide.
          </p>
        </div>

        {/* Marquee Wrapper with soft edge fading mask */}
        <div 
          className="relative w-full max-w-[100vw] overflow-hidden py-4" 
          style={{ 
            WebkitMaskImage: 'linear-gradient(to right, transparent, black 15%, black 85%, transparent)', 
            maskImage: 'linear-gradient(to right, transparent, black 15%, black 85%, transparent)' 
          }}
        >
          {/* Moving Lane */}
          <div className="flex w-max space-x-6 animate-[scrollLeft_45s_linear_infinite] hover:[animation-play-state:paused] px-6">
            {marqueeItems.map((item, idx) => (
              <div
                key={idx}
                className="w-[320px] sm:w-[420px] h-[260px] flex-shrink-0 flex flex-col justify-between relative bg-[#0b2145]/40 backdrop-blur-xl border border-white/5 rounded-3xl p-6 sm:p-8 hover:bg-[#0b2145]/60 hover:border-[#00cfff]/30 hover:shadow-[0_20px_40px_rgba(0,207,255,0.1)] transition-all duration-300 hover:-translate-y-1.5 cursor-grab active:cursor-grabbing"
              >
                {/* Card top border glow */}
                <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-[#00cfff]/25 to-transparent rounded-t-3xl"></div>

                <div>
                  <div className="flex items-center justify-between mb-4">
                    {/* Category Tag */}
                    <span className="text-[10px] font-bold uppercase tracking-wider text-[#00cfff] bg-[#00cfff]/10 border border-[#00cfff]/20 px-2.5 py-1 rounded-full">
                      {item.category}
                    </span>

                    {/* Rating Stars */}
                    <div className="flex gap-0.5">
                      {[...Array(item.stars)].map((_, i) => (
                        <svg
                          key={i}
                          className="w-4 h-4 text-amber-400"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                        </svg>
                      ))}
                    </div>
                  </div>

                  {/* Review Text */}
                  <p className="text-slate-200 text-sm sm:text-base font-medium leading-relaxed italic line-clamp-4">
                    "{item.text}"
                  </p>
                </div>

                {/* Profile details */}
                <div className="flex items-center gap-3 pt-4 border-t border-white/5">
                  {/* Styled Avatar Initials */}
                  <div className={`w-10 h-10 rounded-full bg-gradient-to-br ${item.color} flex items-center justify-center text-white font-extrabold text-xs shadow-sm shrink-0`}>
                    {item.initials}
                  </div>
                  <div>
                    <h4 className="text-white font-bold text-sm tracking-tight">
                      {item.name}
                    </h4>
                    <p className="text-slate-400 text-xs font-semibold mt-0.5">
                      {item.role}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Animation keyframes style injection */}
      <style>{`
        @keyframes scrollLeft {
          from { transform: translateX(0); }
          to { transform: translateX(calc(-100% / 4)); }
        }
      `}</style>
    </section>
  )
}
